import { ChangeDetectionStrategy, Component, Injector, OnDestroy, TemplateRef, ViewChild } from "@angular/core";
import { IMeetingCustomData } from "@common/ADAPT.Common.Model/organisation/meeting";
import { MeetingAgendaItem, MeetingAgendaItemType } from "@common/ADAPT.Common.Model/organisation/meeting-agenda-item";
import { MeetingAgendaTemplate } from "@common/ADAPT.Common.Model/organisation/meeting-agenda-template";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { Logger } from "@common/lib/logger/logger";
import { SentryLogProvider } from "@common/lib/logger/sentry-log-provider";
import { Trace } from "@common/lib/logger/trace";
import { ErrorHandlingUtilities } from "@common/lib/utilities/error-handling-utilities";
import { IMeetingAgendaDetail, MeetingAgendaType, MeetingsService } from "@org-common/lib/meetings/meetings.service";
import { CommonTeamsAuthService } from "@org-common/lib/teams/common-teams-auth.service";
import { IWorkflowStepFooterTemplate, WorkflowStepComponent } from "@org-common/lib/workflow/workflow-component-registry";
import { EMPTY, lastValueFrom, of, Subject } from "rxjs";
import { catchError, switchMap, tap } from "rxjs/operators";
import { ScheduleMeetingWorkflowStepBaseComponent } from "../schedule-meeting-workflow-step-base.component";

@WorkflowStepComponent("adapt-schedule-meeting-select-agenda-step")
@Component({
    selector: "adapt-schedule-meeting-select-agenda-step",
    templateUrl: "./schedule-meeting-select-agenda-step.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class ScheduleMeetingSelectAgendaStepComponent extends ScheduleMeetingWorkflowStepBaseComponent implements OnDestroy {
    public readonly ConfigureTeam = CommonTeamsAuthService.ConfigureTeam;

    public workflowGoToStep = new Subject<number>();
    public workflowStepFinishCurrent = new Subject<void>();

    public agendaItems: MeetingAgendaItem[] = [];
    public selectedAgendaTemplate?: IMeetingAgendaDetail;
    public newAgendaTemplate?: MeetingAgendaTemplate;
    private importedAgendaItems = false;
    private entitiesEmitted = true; // flag to indicate the entities are emitted and managed by workflow run dialog -> don't have to discard anymore

    @ViewChild("startMeetingNowButton")
    public set startMeetingNowButton(ref: TemplateRef<any>) {
        if (!this.allFooterTemplates.find((t) => t.template === ref)) {
            this.allFooterTemplates.push({ alignment: "right", template: ref });
            this.workflowStepFooterTemplates.next(this.allFooterTemplates);
        }
    }

    @ViewChild("createAgendaButton")
    public set createAgendaButton(ref: TemplateRef<any>) {
        if (!this.allFooterTemplates.find((t) => t.template === ref)) {
            this.allFooterTemplates.push({ alignment: "left", template: ref });
            this.workflowStepFooterTemplates.next(this.allFooterTemplates);
        }
    }

    private allFooterTemplates: IWorkflowStepFooterTemplate[] = [];

    public constructor(
        injector: Injector,
        protected meetingsService: MeetingsService,
        private commonDataService: CommonDataService,
    ) {
        super(injector);
    }

    public ngOnDestroy() {
        super.ngOnDestroy();
        if (!this.entitiesEmitted) {
            this.commonDataService.rejectChanges(this.runData.meeting.extensions.getAllMeetingEntities()).subscribe();
        }
    }

    public get agendaHasItems() {
        return this.agendaItems.length > 0;
    }

    public get startMeetingNowTooltip() {
        if (!this.selectedAgendaTemplate) {
            return "Select a meeting agenda first";
        }

        if (this.agendaHasItems) {
            return this.nonStartableMeetingInfo;
        }

        return undefined;
    }

    public get stepIsValid() {
        return this.agendaHasItems && this.allEntitiesAreValid;
    }

    public async workflowStepOnInit() {
        super.workflowStepOnInit();

        // don't kick the user to the right step if they've manually gone back
        if (!this.runData.hasGoneBack) {
            // rescheduling, need to jump to the schedule step
            if (this.runData.rescheduling) {
                this.workflowGoToStep.next(2);
                return;
            }

            // check if there is an agenda already, if so it could be a restored dialog.
            const hasAgenda = this.runData.meeting.meetingAgendaItems.length > 0;
            const isNewMeeting = this.runData.meeting.entityAspect.entityState.isAdded();
            const customData = this.runData.meeting.extensions.getCustomData<IMeetingCustomData>();

            // editing an already existing meeting or imported from outlook, skip to the customise agenda step
            if (!isNewMeeting || hasAgenda || customData.imported) {
                this.workflowGoToStep.next(1);
                return;
            }
        }

        await lastValueFrom(this.meetingsService.initialiseMeeting(this.runData.meeting));

        // add the meeting entities to changes so we can discard them
        if (this.runData.isCreatingNewMeeting) {
            // newly created meeting won't emit entities so it won't prompt the discard dialog without change
            this.runData.isCreatingNewMeeting = false;
            this.entitiesEmitted = false; // default to true so it won't discard when restoring and moving to next step
        } else {
            this.emitMeetingEntitiesChanged();
        }

        this.runData.hasGoneBack = false;
        this.isInitialised = true;
    }

    public emitMeetingEntitiesChanged() {
        super.emitMeetingEntitiesChanged();
        this.entitiesEmitted = true; // won't have to worry about discarding changes anymore
    }

    public workflowStepNext() {
        return this.populateMeetingAgenda();
    }

    public createOwnAgenda() {
        this.selectedAgendaTemplate = undefined;
        this.emitMeetingEntitiesChanged();
        this.workflowStepFinishCurrent.next();
    }

    public importRecentMeeting() {
        this.meetingsUiService.importMeetingAgenda({ saveOnClose: false, currentMeeting: this.runData.meeting, recentMeetingOnly: true }).pipe(
            tap((items) => {
                this.selectedAgendaTemplate = undefined;
                this.runData.selectedTemplateId = undefined;
                this.runData.meeting.name = items[0]?.meeting?.name || "Enter meeting name";
                if (items[0]?.meeting?.supplementaryData?.purpose) {
                    this.runData.meeting.supplementaryData!.purpose = items[0].meeting.supplementaryData.purpose;
                }
            }),
            switchMap((items) => this.meetingsService.importAgendaItems(items, this.runData.meeting.meetingId)),
            this.takeUntilDestroyed(),
        ).subscribe(() => {
            this.emitMeetingEntitiesChanged();
            this.workflowGoToStep.next(1);
        });
    }

    @Autobind
    public populateAndRunMeetingNow() {
        return this.populateMeetingAgenda().pipe(
            switchMap(() => this.runMeetingNow(true)),
            catchError((err) => {
                const message = ErrorHandlingUtilities.getHttpResponseMessage(err);
                this.workflowStepErrorMessage.next(message);

                this.submitting = false;
                this.validateEntities();
                return EMPTY;
            }),
        );
    }

    @Autobind
    public createNewAgenda() {
        return this.meetingsService.createAgendaTemplate(this.runData.meeting.teamId).pipe(
            switchMap((agendaTemplate) => this.meetingsUiService.editAgendaTemplate(agendaTemplate)),
            tap((agendaTemplate) => this.newAgendaTemplate = agendaTemplate),
            this.takeUntilDestroyed(),
        );
    }

    public async onAgendaSelected(agenda: IMeetingAgendaDetail) {
        if (!this.submitting) {
            this.newAgendaTemplate = undefined;
            this.selectedAgendaTemplate = agenda;
            this.runData.selectedTemplateId = agenda.meetingTemplate.meetingAgendaTemplateId;
            this.runData.meeting.name = agenda.meetingTemplate.name;
            if (this.runData.meeting.supplementaryData) {
                this.runData.meeting.supplementaryData.purpose = agenda.meetingTemplate.meetingDescription ?? "";
            }
            this.agendaItems = await lastValueFrom(this.getAgendaItemsForSelectedTemplate());
            this.validateEntities();
            this.emitMeetingEntitiesChanged(); // provided a template is selected, you will be prompted to discard changes
        }
    }

    private getAgendaItemsForSelectedTemplate() {
        if (!this.selectedAgendaTemplate) {
            return of([]);
        }

        if (this.selectedAgendaTemplate.type === MeetingAgendaType.AdHoc) {
            const adHocAgendaItem = new MeetingAgendaItem();
            adHocAgendaItem.ordinal = 0;
            adHocAgendaItem.name = "Ad-hoc meeting";
            adHocAgendaItem.type = MeetingAgendaItemType.AgendaItem;
            return of([adHocAgendaItem]);
        }

        return this.meetingsService.getAgendaItemsForMeetingAgendaTemplate(this.selectedAgendaTemplate.meetingTemplate.meetingAgendaTemplateId);
    }

    private populateMeetingAgenda() {
        if (!this.selectedAgendaTemplate) {
            return of([]);
        }

        if (this.importedAgendaItems) {
            const sentryLogger = Logger.getLogProviderOfType(SentryLogProvider);
            sentryLogger?.write({
                level: Trace.Error,
                timestamp: new Date(),
                moduleId: "ScheduleMeetingSelectAgendaStepComponent",
                message: "Multiple calls to populateMeetingAgenda have occurred",
                data: [this.runData],
            });

            return of([]);
        }

        this.submitting = true;
        this.isValid.next(false);

        return this.meetingsService.importAgendaItems(this.agendaItems, this.runData.meeting.meetingId).pipe(
            tap(() => {
                this.importedAgendaItems = true;
                this.emitMeetingEntitiesChanged();
            }),
        );
    }
}
